import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createTeflLecComment } from '../../../../api/tefl/tefl-lec-comments/create-tefl-lec-comment'
import { deleteTeflLecComments } from '../../../../api/tefl/tefl-lec-comments/delete-tefl-lec-comment'
import { getTeflLecComments } from '../../../../api/tefl/tefl-lec-comments/get-tefl-lec-comments'
import { updateTeflLecComment } from '../../../../api/tefl/tefl-lec-comments/update-tefl-lec-comment'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { TeflLecComment } from '../../../../lib/types/models/tefl-lec-comment/tefl-lec-comment'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import { mapTeflLecCommentToInput } from '../../tefl/utils'
import TeflLecCommentForm from './ol-tefl-lec-comment-form'

type Props = {
  id: number
}

const TeflLecCommentTable = ({ id }: Props) => {
  const queryClient = useQueryClient()

  const { modalState, closeModel, selectedData, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    TeflLecComment
  >(['create', 'edit', 'delete'])

  const { data: teflLecComments } = useQuery({
    queryKey: ['tefl-lec-comments'],
    queryFn: () => getTeflLecComments({ teflId: id }),
    enabled: !!id
  })

  const { mutateAsync: deleteComment, isPending: isLoading } = useMutation({
    mutationFn: deleteTeflLecComments,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment deleted successfully')
      queryClient.invalidateQueries({
        queryKey: ['tefl-lec-comments']
      })
      closeModel('delete')
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: createTeflLecComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment added successfully')
      queryClient.invalidateQueries({
        queryKey: ['tefl-lec-comments']
      })
      closeModel('create')
    }
  })

  const { mutateAsync: updateComment } = useMutation({
    mutationFn: updateTeflLecComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['tefl-lec-comments']
      })
      closeModel('edit')
    }
  })

  const columns: Column<TeflLecComment>[] = [
    {
      enableSorting: true,
      header: 'Commentaire',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <Typography
          variant="tag"
          color={tailwindColors.black.DEFAULT}
          className="text-wrap px-3 py-4"
        >
          {row.original.comment}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Commenté par',
      accessorKey: 'commentedBy',
      cell: ({ row }) => (
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {toFullName(row.original.commentedBy.firstName, row.original.commentedBy.lastName)}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Visibilité',
      accessorKey: 'isVisibleForTheYear',
      cell: ({ row }) => (
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {row.original.isVisibleForTheYear ? 'Année en cours' : 'Indéfiniment'}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Date & heure',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.updatedAt)} - ${DateFormatter.HHmm(row.original.updatedAt)}`}
          variant="grey"
        />
      )
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <Box className="bg-white-hover rounded-lg border border-grey-light p-0">
      <ActionTableWrapper
        title={'Commentaires'}
        transparent
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Ajouter un commentaire'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<TeflLecComment>
          manualSorting={false}
          columns={columns}
          data={teflLecComments?.data ?? []}
          rowCount={teflLecComments?.data.length ?? 0}
          paginated={false}
        />

        <Modal
          isOpen={modalState.create}
          title={'Écrire un commentaire'}
          size="medium"
          hideActions
          icon={MessageIcon}
          onCancel={() => closeModel('create')}
        >
          <TeflLecCommentForm
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: async (values) => {
                await mutateAsync({ ...values, teflId: id })
              }
            })}
            defaultValues={{
              isVisibleForTheYear: false,
              sharedToOl: false
            }}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Modifier un commentaire'}
            hideActions
            icon={MessageIcon}
            size="medium"
            onCancel={() => closeModel('edit')}
          >
            <TeflLecCommentForm
              defaultValues={mapTeflLecCommentToInput(selectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: async (values) => {
                  await updateComment({
                    ...values,
                    teflId: id,
                    id: selectedData.id
                  })
                }
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: async () => {
                await deleteComment({ teflId: id, ids: [selectedData.id] })
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Supprimer un commentaire'}
            icon={DeleteIcon}
            isLoading={isLoading}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </Box>
  )
}

export default TeflLecCommentTable
